import { FileIcon, useDownloadFile, useDownloadZip } from '@newfront-insurance/admin-ui/files';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { AlertCircle, Download, Eye, Loader2 } from 'lucide-react';
import { useEffect, useId, useMemo } from 'react';

import { getArtifactPreviewMenuItemId, useArtifactPreviewSafe } from './artifact-preview/artifact-preview-provider';
import type { ArtifactPreviewMenuItem } from './artifact-preview/types';
import {
  getArtifactPreviewFileType,
  getArtifactPreviewProviderId,
  getArtifactPreviewProviderLabel,
} from './artifact-preview/types';
import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';

const MAX_FAILURE_REASON_TITLE_LENGTH = 100;
const GENERATED_FILES_ZIP_FILENAME = 'nova-generated-files.zip';
const GENERATED_FILE_WIDGET_STYLE = {
  background:
    'linear-gradient(#effcfa, #effcfa) padding-box, linear-gradient(117deg, #3adcc9 0%, #dbc1f4 100%) border-box',
  border: '2px solid transparent',
};

export interface GeneratedFileListItem {
  uuid: string | null;
  filename: string;
  isPending?: boolean;
  failed?: boolean;
  failureReason?: string | null;
}

function getFailureTitle(failureReason?: string | null) {
  return (failureReason ?? 'File unavailable').slice(0, MAX_FAILURE_REASON_TITLE_LENGTH);
}

function getFileDocumentLabel(filename: string) {
  const extension = (getFileExtension(filename) ?? '').toLowerCase();

  if (extension === 'pdf') return 'PDF Document';
  if (extension === 'doc' || extension === 'docx') return 'Word Document';
  if (extension === 'ppt' || extension === 'pptx') return 'PowerPoint Presentation';
  if (extension === 'xls' || extension === 'xlsx') return 'Excel Workbook';
  if (extension === 'zip') return 'Compressed Archive';
  if (extension === 'txt') return 'Text Document';
  if (extension === 'html' || extension === 'htm') return 'HTML Document';

  return extension ? `${extension.toUpperCase()} File` : 'File';
}

function getSingleFileTitle(file: GeneratedFileListItem, canPreview: boolean) {
  const documentLabel = getFileDocumentLabel(file.filename);

  if (file.failed) return `${documentLabel} unavailable`;
  if (file.isPending || !file.uuid) return `${documentLabel} is being generated`;

  return `Your ${documentLabel} is ready to ${canPreview ? 'view' : 'download'}`;
}

function isDownloadableGeneratedFile(file: GeneratedFileListItem): file is GeneratedFileListItem & { uuid: string } {
  return Boolean(file.uuid && !file.failed && !file.isPending);
}

export function GeneratedFilesList({
  files,
  messageUuid,
  source = 'generated_file',
}: {
  files: GeneratedFileListItem[];
  messageUuid?: string;
  source?: string;
}) {
  const ownerId = useId();
  const artifactPreview = useArtifactPreviewSafe();
  const isArtifactPreviewEnabled = artifactPreview !== null;
  const openArtifactPreview = artifactPreview?.openArtifactPreview;
  const previewingArtifactId = artifactPreview?.previewingArtifactId ?? null;
  const registerArtifacts = artifactPreview?.registerArtifacts;
  const previewArtifacts = useMemo<ArtifactPreviewMenuItem[]>(() => {
    if (!isArtifactPreviewEnabled) return [];

    return files.flatMap((file) => {
      if (!file.uuid || file.failed || file.isPending) return [];

      const fileType = getArtifactPreviewFileType(file.filename);
      if (!fileType) return [];

      const providerId = getArtifactPreviewProviderId(fileType);

      return [
        {
          fileType,
          filename: file.filename,
          id: getArtifactPreviewMenuItemId({
            filename: file.filename,
            messageUuid,
            source,
            uuid: file.uuid,
          }),
          messageUuid,
          providerId,
          providerLabel: getArtifactPreviewProviderLabel(fileType),
          source,
          uuid: file.uuid,
        },
      ];
    });
  }, [files, isArtifactPreviewEnabled, messageUuid, source]);
  const previewArtifactsByFileUuid = useMemo(
    () => new Map(previewArtifacts.map((artifact) => [artifact.uuid, artifact])),
    [previewArtifacts],
  );

  useEffect(() => {
    if (!registerArtifacts) return undefined;
    return registerArtifacts(ownerId, previewArtifacts);
  }, [ownerId, previewArtifacts, registerArtifacts]);

  const handlePreview = (previewArtifact: ArtifactPreviewMenuItem | undefined) => {
    if (previewArtifact && openArtifactPreview) {
      void openArtifactPreview(previewArtifact);
    }
  };

  const previewableFileCount = files.reduce((count, file) => {
    if (!file.uuid || !previewArtifactsByFileUuid.has(file.uuid)) return count;
    return count + 1;
  }, 0);
  const firstFile = files[0];

  if (!firstFile) return null;

  return (
    <TooltipProvider delayDuration={0}>
      {files.length === 1 ? (
        <GeneratedFileSingleWidget
          file={firstFile}
          messageUuid={messageUuid}
          onPreview={handlePreview}
          previewArtifact={firstFile.uuid ? previewArtifactsByFileUuid.get(firstFile.uuid) : undefined}
          isPreviewing={
            firstFile.uuid ? previewingArtifactId === previewArtifactsByFileUuid.get(firstFile.uuid)?.id : false
          }
          source={source}
        />
      ) : (
        <GeneratedFilesGroupWidget
          files={files}
          messageUuid={messageUuid}
          onPreview={handlePreview}
          previewArtifactsByFileUuid={previewArtifactsByFileUuid}
          previewableFileCount={previewableFileCount}
          previewingArtifactId={previewingArtifactId}
          source={source}
        />
      )}
    </TooltipProvider>
  );
}

function GeneratedFileSingleWidget({
  file,
  messageUuid,
  isPreviewing,
  onPreview,
  previewArtifact,
  source,
}: {
  file: GeneratedFileListItem;
  messageUuid?: string;
  isPreviewing: boolean;
  onPreview: (previewArtifact: ArtifactPreviewMenuItem | undefined) => void;
  previewArtifact?: ArtifactPreviewMenuItem;
  source: string;
}) {
  const canPreview = Boolean(previewArtifact);

  return (
    <div
      className="flex w-full max-w-[661px] flex-col items-center justify-center overflow-hidden rounded-[16px] px-5 py-4"
      style={GENERATED_FILE_WIDGET_STYLE}
    >
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="truncate text-sm leading-5 font-semibold text-[#262d46]">
            {getSingleFileTitle(file, canPreview)}
          </p>
          <p className="truncate text-xs leading-4 text-[#546a83]">{file.filename}</p>
        </div>
        <GeneratedFileActions
          file={file}
          isPreviewing={isPreviewing}
          messageUuid={messageUuid}
          onPreview={() => onPreview(previewArtifact)}
          previewArtifact={previewArtifact}
          source={source}
        />
      </div>
    </div>
  );
}

function GeneratedFilesGroupWidget({
  files,
  messageUuid,
  onPreview,
  previewArtifactsByFileUuid,
  previewableFileCount,
  previewingArtifactId,
  source,
}: {
  files: GeneratedFileListItem[];
  messageUuid?: string;
  onPreview: (previewArtifact: ArtifactPreviewMenuItem | undefined) => void;
  previewArtifactsByFileUuid: Map<string, ArtifactPreviewMenuItem>;
  previewableFileCount: number;
  previewingArtifactId: string | null;
  source: string;
}) {
  const { downloadZip, isDownloading } = useDownloadZip();
  const analytics = useAnalytics();
  const downloadableFiles = files.filter(isDownloadableGeneratedFile);
  const subtitle =
    previewableFileCount > 0 ? `${previewableFileCount} viewable · ${files.length} total` : `${files.length} total`;
  const canDownloadAll = downloadableFiles.length > 0 && !isDownloading;

  const handleDownloadAll = () => {
    downloadableFiles.forEach((file) => {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
        fileExtension: getFileExtension(file.filename),
        fileUuid: file.uuid,
        messageUuid,
        source,
      });
    });

    void downloadZip(
      downloadableFiles.map((file) => file.uuid),
      { filename: GENERATED_FILES_ZIP_FILENAME },
    );
  };

  return (
    <div
      className="flex w-full max-w-[665px] flex-col gap-4 overflow-hidden rounded-[16px] px-5 py-4"
      style={GENERATED_FILE_WIDGET_STYLE}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm leading-5 font-semibold text-[#262d46]">{files.length} files ready</p>
          <p className="text-xs leading-4 text-[#546a83]">{subtitle}</p>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={!canDownloadAll}
              aria-busy={isDownloading}
              aria-label="Download all generated files"
              className={cn(
                'flex shrink-0 items-center gap-2 text-sm leading-5 font-semibold text-[#0957c3] transition-colors',
                'disabled:cursor-default disabled:opacity-50',
              )}
              onClick={handleDownloadAll}
            >
              {isDownloading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-5 text-[#546a83]" />
              )}
              <span>Download all</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>Download all generated files</TooltipContent>
        </Tooltip>
      </div>
      <div className="h-px bg-[#dee2e7]" />
      <div className="flex flex-col gap-4">
        {files.map((file) => {
          const previewArtifact = file.uuid ? previewArtifactsByFileUuid.get(file.uuid) : undefined;

          return (
            <GeneratedFileRow
              key={`${file.filename}-${file.uuid ?? 'pending'}`}
              file={file}
              messageUuid={messageUuid}
              onPreview={() => onPreview(previewArtifact)}
              previewArtifact={previewArtifact}
              isPreviewing={previewArtifact ? previewingArtifactId === previewArtifact.id : false}
              source={source}
            />
          );
        })}
      </div>
    </div>
  );
}

function GeneratedFileRow({
  file,
  messageUuid,
  isPreviewing,
  onPreview,
  previewArtifact,
  source,
}: {
  file: GeneratedFileListItem;
  messageUuid?: string;
  isPreviewing: boolean;
  onPreview: () => void;
  previewArtifact?: ArtifactPreviewMenuItem;
  source: string;
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-4">
        <span className="shrink-0 [&>div]:!h-[35px] [&>div]:!rounded-[4px] [&>div]:!bg-[#f4f8fe] [&>div]:!shadow-[0_1px_2px_rgba(0,0,0,0.25)] [&>div>div]:!bg-[#546a83]">
          <FileIcon filename={file.filename} hasBackground hasBoxShadow={false} />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <p className="truncate text-sm leading-5 font-semibold text-[#262d46]">{file.filename}</p>
          <p className="truncate text-xs leading-4 text-[#546a83]">{getFileDocumentLabel(file.filename)}</p>
        </div>
      </div>
      <GeneratedFileActions
        file={file}
        isPreviewing={isPreviewing}
        messageUuid={messageUuid}
        onPreview={onPreview}
        previewArtifact={previewArtifact}
        source={source}
      />
    </div>
  );
}

function GeneratedFileActions({
  file,
  messageUuid,
  isPreviewing,
  onPreview,
  previewArtifact,
  source,
}: {
  file: GeneratedFileListItem;
  messageUuid?: string;
  isPreviewing: boolean;
  onPreview: () => void;
  previewArtifact?: ArtifactPreviewMenuItem;
  source: string;
}) {
  const { download, isDownloading } = useDownloadFile({});
  const analytics = useAnalytics();
  const isFailed = Boolean(file.failed);
  const isPending = !isFailed && (file.isPending || !file.uuid);
  const canPreview = Boolean(previewArtifact);
  const isBusy = isDownloading || isPending || isPreviewing;
  const isDisabled = isBusy || isFailed || !file.uuid;
  const failureTitle = isFailed ? getFailureTitle(file.failureReason) : undefined;

  const handleClick = () => {
    if (file.uuid && !isFailed) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
        fileExtension: getFileExtension(file.filename),
        fileUuid: file.uuid,
        messageUuid,
        source,
      });
      download(file.uuid);
    }
  };

  return (
    <div className={cn('flex shrink-0 items-center justify-end gap-4', { 'opacity-50': isPending })}>
      {canPreview && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-sm text-[#546a83] transition-colors',
                'disabled:cursor-progress disabled:opacity-80',
              )}
              disabled={isBusy}
              aria-label={`Preview ${file.filename} in ${previewArtifact?.providerLabel}`}
              aria-busy={isPreviewing}
              onClick={onPreview}
            >
              {isPreviewing ? <Loader2 className="size-4 animate-spin" /> : <Eye className="size-5" />}
            </button>
          </TooltipTrigger>
          <TooltipContent>Preview</TooltipContent>
        </Tooltip>
      )}
      <button
        type="button"
        data-testid="generated-file-download-button"
        onClick={handleClick}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={isPending || isDownloading}
        aria-label={`Download ${file.filename}`}
        title={failureTitle}
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-sm text-[#546a83] transition-colors',
          'disabled:cursor-default disabled:opacity-80',
        )}
      >
        {isPending || (isDownloading && !isPreviewing) ? (
          <Loader2 className="size-4 animate-spin" />
        ) : isFailed ? (
          <>
            <AlertCircle className="text-destructive size-4 shrink-0" />
            <span className="sr-only" data-testid="generated-file-unavailable">
              Unavailable
            </span>
          </>
        ) : (
          <Download className="size-5 shrink-0" />
        )}
      </button>
    </div>
  );
}
